'use client'

import { useEffect, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { toast } from '@/lib/toast'
import { navigateUrl } from '@/utils/client-request'
import { copyToClipboard } from '@/utils/share'

export const IN_APP_BROWSER_LABELS = {
  message: 'Messenger, Facebook, Instagram дотроос банкны апп нээгдэхгүй байж болно. Хуудсыг хөтөч дээрээ нээгээд төлбөрөө үргэлжлүүлнэ үү.',
  action: 'Хөтөч дээр нээх',
  hint: 'Хөтөч нээгдэхгүй бол холбоос хуулагдах тул Safari эсвэл Chrome дээр буулгаж нээнэ үү. Эсвэл дэлгэцийн буланд байрлах ⋯ цэснээс "Open in external browser"-ийг сонгоно уу.',
  copied: 'Холбоос хуулагдлаа. Safari эсвэл Chrome дээр буулгаж нээнэ үү.',
  blocked: 'Хөтөч нээгдсэнгүй. ⋯ цэснээс "Open in external browser"-ийг сонгоно уу.',
}

type Target = { order?: any, giftCard?: any }

const IN_APP_PATTERN = /FBAN|FBAV|FB_IAB|FB4A|FBIOS|Instagram/i
const MESSENGER_PATTERN = /Messenger|Orca/i
const IOS_PATTERN = /iPhone|iPad|iPod/i
const ANDROID_PATTERN = /Android/i
const OPEN_TIMEOUT = 1500

const subscribeNoop = () => () => {}
const getUserAgent = () => navigator.userAgent
const getServerUserAgent = () => ''

const getPagePath = ({ order, giftCard }: Target, pathname: string | null) => {
  if (giftCard?.customId) return `/gift/pay/${giftCard.customId}`
  if (!order?.customId) return ''
  if (order.phone && pathname?.includes(`/${order.phone}`)) return `/order/${order.customId}/${order.phone}`
  return `/order/${order.customId}`
}

const buildBrowserUrl = (userAgent: string, url: string) => {
  if (IOS_PATTERN.test(userAgent)) return url.replace(/^http/, 'x-safari-http')
  if (ANDROID_PATTERN.test(userAgent)) {
    const scheme = window.location.protocol.replace(':', '')
    return `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=${scheme};package=com.android.chrome;end`
  }
  return url
}

const needsFullLoad = (pageUrl: string) => {
  try {
    const loadedUrl = performance.getEntriesByType('navigation')[0]?.name
    if (!loadedUrl) return false
    const target = new URL(pageUrl).pathname
    if (new URL(loadedUrl).pathname === target && window.location.pathname === target) return false
    const key = `in-app-full-load:${target}`
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    return false
  }
}

export function useInAppBrowser(target: Target) {
  const pathname = usePathname()
  const userAgent = useSyncExternalStore(subscribeNoop, getUserAgent, getServerUserAgent)
  const isInAppBrowser = IN_APP_PATTERN.test(userAgent)
  const appLinksBlocked = isInAppBrowser && (IOS_PATTERN.test(userAgent) || MESSENGER_PATTERN.test(userAgent))
  const path = getPagePath(target, pathname)
  const pageUrl = isInAppBrowser && path ? `${window.location.origin}${navigateUrl(path, pathname)}` : ''
  const browserUrl = pageUrl ? buildBrowserUrl(userAgent, pageUrl) : ''

  useEffect(() => {
    if (!pageUrl || !needsFullLoad(pageUrl)) return
    window.location.replace(pageUrl)
  }, [pageUrl])

  const openInBrowser = () => {
    const copying = copyToClipboard(pageUrl)
    let left = false
    const markLeft = () => { if (document.visibilityState === 'hidden') left = true }
    document.addEventListener('visibilitychange', markLeft)
    window.setTimeout(async () => {
      document.removeEventListener('visibilitychange', markLeft)
      if (left) return
      if (await copying) toast.success(IN_APP_BROWSER_LABELS.copied)
      else toast.info(IN_APP_BROWSER_LABELS.blocked)
    }, OPEN_TIMEOUT)
  }

  return { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser }
}
